import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";

import LoadingIcon from "../../../../components/LoadingIcon";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";

import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import ExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import google_step_1 from "./images/Google_step_1.png";
import google_step_10 from "./images/Google_step_10.png";
import google_step_2 from "./images/Google_step_2.png";
import google_step_3 from "./images/Google_step_3.png";
import google_step_4 from "./images/Google_step_4.png";
import google_step_5 from "./images/Google_step_5.png";
import google_step_6 from "./images/Google_step_6.png";
import google_step_7 from "./images/Google_step_7.png";
import google_step_8 from "./images/Google_step_8.png";
import google_step_9 from "./images/Google_step_9.png";

const GoogleIntegration = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("google");
  const [loading, setLoading] = useState(false);
  const [authUrl, setAuthUrl] = useState("");
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [manualExpanded, setManualExpanded] = useState(false);
  const [optionalExpanded, setOptionalExpanded] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { fetchGoogleAuthUrl, trackEvent, checkGoogleRemediationPermissions } =
    useAPI();
  const colors = useColors();

  // State for remediation permissions check
  const [checkingPermissions, setCheckingPermissions] = useState(false);

  useEffect(() => {
    if (!isIntegrated) {
      setLoading(true);
      fetchGoogleAuthUrl().then((response) => {
        setAuthUrl(response.data.url);
        setLoading(false);
      });
    }
  }, [isIntegrated]);

  const handleManualAccordionChange = () => {
    if (!manualExpanded) {
      trackEvent(
        `User expanded Google Workspace manual integration flow`
      ).then();
    }
    setManualExpanded(!manualExpanded);
  };

  const handleOptionalAccordionChange = () => {
    if (!optionalExpanded) {
      trackEvent(
        `User expanded Google Workspace optional domain-wide delegation flow`
      ).then();
    }
    setOptionalExpanded(!optionalExpanded);
  };

  const handleCheckRemediationPermissions = async () => {
    setCheckingPermissions(true);

    try {
      const response = await checkGoogleRemediationPermissions();
      const hasPermissions = response.data.has_remediation_permissions;

      if (hasPermissions) {
        Toast.success("Remediation permissions are properly configured");
      } else {
        Toast.warning(
          "Remediation permissions are not configured. Please follow the instructions below to configure them."
        );
      }

      trackEvent("User checked Google remediation permissions").then();
    } catch (error) {
      Toast.error("Failed to check remediation permissions. Please try again.");
    } finally {
      setCheckingPermissions(false);
    }
  };

  if (loading || isIntegratedLoading) {
    return <LoadingIcon />;
  }

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, Breeze will scan your Google Workspace for misconfigurations and vulnerabilities."
            : ""
        }
        image={logos.GoogleLogo}
        name={"Google Workspace"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Fragment>
              <Typography variant={"h4"}>
                Google Workspace is integrated!
              </Typography>
            </Fragment>
          ) : (
            <>
              <Box
                sx={{
                  mb: 2,
                }}
              >
                {guideMode && <IntegrationGuideAlert />}
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <Typography variant="h4" mb="15px">
                Quick Authorization Flow
              </Typography>
              <GuidelineStepByStep>
                <GuidelineStep title="Authorize Breeze app to Google Workspace with an account that have Super-Admin permissions">
                  {authUrl && (
                    <PrimaryButton
                      sx={{
                        width: "150px",
                      }}
                      onClick={() => window.open(authUrl, "_blank")}
                      disabled={guideMode}
                    >
                      Authorize
                    </PrimaryButton>
                  )}
                </GuidelineStep>
              </GuidelineStepByStep>
              <Typography variant="h4" mt="30px">
                Manual Flow
              </Typography>
              <Box mb="20px" mt="15px" minWidth="100%">
                <Accordion
                  expanded={manualExpanded}
                  onChange={handleManualAccordionChange}
                  sx={{
                    boxShadow: manualExpanded
                      ? "none"
                      : "0px 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:before": { display: "none" },
                    backgroundColor: manualExpanded
                      ? "transparent"
                      : colors.grey_100,
                    overflowY: "auto",
                    borderRadius: manualExpanded ? "4px 4px 0 0" : "4px",
                    border: manualExpanded
                      ? `1px solid ${colors.grey_200}`
                      : "none",
                    borderBottom: manualExpanded ? "none" : undefined,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: manualExpanded
                        ? "transparent"
                        : colors.grey_200,
                    },
                  }}
                  TransitionProps={{ timeout: 150 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="manual-instructions-content"
                    id="manual-instructions-header"
                    sx={{
                      padding: "12px 16px",
                      minHeight: "48px",
                      "& .MuiAccordionSummary-content": { margin: 0 },
                      cursor: "pointer",
                    }}
                  >
                    <Typography variant={"h6"}>
                      Instructions for manual configuration of Breeze with
                      Google Workspace
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: "16px",
                      maxHeight: "60vh",
                      overflowY: "auto",
                      overflowX: "hidden",
                      backgroundColor: colors.white,
                      borderLeft: `1px solid ${colors.grey_200}`,
                      borderRight: `1px solid ${colors.grey_200}`,
                      borderBottom: `1px solid ${colors.grey_200}`,
                      borderRadius: "0 0 4px 4px",
                    }}
                  >
                    <Box mb="20px">
                      <Grid container spacing={2} direction={"row"}>
                        <Grid item>
                          <SecondaryButton onClick={handleExpandAll}>
                            Expand All Screenshots
                          </SecondaryButton>
                        </Grid>
                        <Grid item>
                          <SecondaryButton onClick={handleCollapseAll}>
                            Collapse All Screenshots
                          </SecondaryButton>
                        </Grid>
                      </Grid>
                    </Box>
                    <GuidelineStepByStep>
                      <GuidelineStep title="Sign in with an administrator account to the Google Admin console." />

                      <GuidelineStep
                        title={
                          <ExternalLink
                            href="https://admin.google.com/ac/owl"
                            text="Go to Menu > "
                            linkText="Security > Access and data control > API controls"
                            sx={{ fontSize: "20px", fontWeight: "600" }}
                          />
                        }
                      >
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_1,
                              altext: "Google Admin Console API Controls page",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Click on Manage Third-Party APP Access">
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_2,
                              altext: "manage third-party app access page",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Click on Configure new app">
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_3,
                              altext: "configure new app page",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep
                        title={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant={"h5"}>
                              Search for Breeze Platform
                            </Typography>
                            <CustomCopyButton
                              copyTitle={"name"}
                              copyText={"Breeze Platform"}
                            />
                          </Box>
                        }
                      >
                        <Typography variant={"body1"}>
                          Select the{" "}
                          <Box
                            component="span"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                              backgroundColor: colors.grey_200,
                              p: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            Breeze Platform
                          </Box>{" "}
                          option with Client ID:{" "}
                          <Box
                            component="span"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                              backgroundColor: colors.grey_200,
                              p: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            ...lnq8gaj12.apps.googleusercontent.com
                          </Box>
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_4,
                              altext: "Search for Breeze Platform",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Select scope for Breeze Platform">
                        <Typography variant={"body1"}>
                          Select "All in {"{"}your_organization{"}"}" Then click
                          on continue
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_5,
                              altext: "Select scope for Breeze Platform",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Allow Access to Google Data">
                        <Typography variant={"body1"}>
                          Select "Trusted" and "Allowlist for exemption from
                          API" and then click on continue
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_6,
                              altext: "Breeze Platform",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Click on Finish">
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_7,
                              altext: "Breeze Platform",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Return to the Breeze platform and complete integration">
                        <Typography variant={"body1"}>
                          After configuring Google Workspace permissions, return
                          to Breeze platform and click the "Authorize" button
                          above to finalize the integration.
                        </Typography>
                      </GuidelineStep>
                    </GuidelineStepByStep>
                  </AccordionDetails>
                </Accordion>
              </Box>
              <Box
                sx={{
                  height: "4px",
                  backgroundColor: colors.primary_dark,
                  marginTop: "80px",
                  borderRadius: "2px",
                  width: "100%",
                }}
              />
              <Typography variant="h4" mt="30px">
                Remediation permissions
              </Typography>
              <Box mb="20px" mt="15px" minWidth="100%">
                <Accordion
                  expanded={optionalExpanded}
                  onChange={handleOptionalAccordionChange}
                  sx={{
                    boxShadow: optionalExpanded
                      ? "none"
                      : "0px 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:before": { display: "none" },
                    backgroundColor: optionalExpanded
                      ? "transparent"
                      : colors.grey_100,
                    overflowY: "auto",
                    borderRadius: optionalExpanded ? "4px 4px 0 0" : "4px",
                    border: optionalExpanded
                      ? `1px solid ${colors.grey_200}`
                      : "none",
                    borderBottom: optionalExpanded ? "none" : undefined,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: optionalExpanded
                        ? "transparent"
                        : colors.grey_200,
                    },
                  }}
                  TransitionProps={{ timeout: 150 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="domain-wide-delegation-content"
                    id="domain-wide-delegation-header"
                    sx={{
                      padding: "12px 16px",
                      minHeight: "48px",
                      "& .MuiAccordionSummary-content": { margin: 0 },
                      cursor: "pointer",
                    }}
                  >
                    <Typography variant={"h6"}>
                      Instructions for configuring remediation permission for
                      Breeze service account.
                      <br />
                      Note: By granting Breeze remediation permissions, Breeze
                      can perform remediation actions on your behalf (e.g.,
                      disable public sharing status for files).
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: "16px",
                      maxHeight: "60vh",
                      overflowY: "auto",
                      overflowX: "hidden",
                      backgroundColor: colors.white,
                      borderLeft: `1px solid ${colors.grey_200}`,
                      borderRight: `1px solid ${colors.grey_200}`,
                      borderBottom: `1px solid ${colors.grey_200}`,
                      borderRadius: "0 0 4px 4px",
                    }}
                  >
                    <Box mb="20px">
                      <Grid container spacing={2} direction={"row"}>
                        <Grid item>
                          <SecondaryButton onClick={handleExpandAll}>
                            Expand All Screenshots
                          </SecondaryButton>
                        </Grid>
                        <Grid item>
                          <SecondaryButton onClick={handleCollapseAll}>
                            Collapse All Screenshots
                          </SecondaryButton>
                        </Grid>
                      </Grid>
                    </Box>
                    <GuidelineStepByStep>
                      <GuidelineStep
                        title={
                          <ExternalLink
                            href="https://admin.google.com/ac/owl/domainwidedelegation"
                            text="Follow this link and Sign in with a super administrator account to the Google Admin console: "
                            linkText=" Manage Domain Wide Delegation"
                            sx={{ fontSize: "20px", fontWeight: "600" }}
                          />
                        }
                      >
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_8,
                              altext:
                                "Google Admin Console Domain Wide Delegation page",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Click on Add new">
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_9,
                              altext: "manage third-party app access page",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>

                      <GuidelineStep title="Enter Breeze Service Account Client ID:">
                        <Typography variant={"body2"}>
                          Client ID:{" "}
                          <Box component="span" sx={{ fontWeight: "bold" }}>
                            106322758873526201732
                          </Box>
                          <CustomCopyButton
                            copyTitle={"Client ID"}
                            copyText={"106322758873526201732"}
                          />
                        </Typography>
                      </GuidelineStep>

                      <GuidelineStep title="Allow Access to Google Data">
                        <Typography variant={"body1"}>
                          Add the required OAuth scope:
                        </Typography>
                        <Box
                          component="div"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            backgroundColor: colors.grey_200,
                            p: 1,
                            borderRadius: 1,
                            mt: 1,
                            wordBreak: "break-all",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {`https://www.googleapis.com/auth/drive`}
                          <CustomCopyButton
                            copyTitle={"OAuth scope"}
                            copyText={"https://www.googleapis.com/auth/drive"}
                          />
                        </Box>
                      </GuidelineStep>
                      <GuidelineStep title="Click Authorize">
                        <ScreenshotAccordion
                          images={[
                            {
                              file: google_step_10,
                              altext:
                                "Google Admin Console Domain Wide Delegation page",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </GuidelineStep>
                    </GuidelineStepByStep>
                    <Box mt="30px">
                      <PrimaryButton
                        onClick={handleCheckRemediationPermissions}
                        disabled={checkingPermissions}
                      >
                        {checkingPermissions
                          ? "Checking..."
                          : "Check Remediation Permissions"}
                      </PrimaryButton>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </>
          )
        }
      />
    </Box>
  );
};

export default GoogleIntegration;
